import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';

/**
 * Thin wrapper over SweetAlert2 toasts for app-wide success/error feedback.
 * Replaces the old UI's custom GlobalSuccessMessage component/service.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
    private translate = inject(TranslateService);

    private toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    private fire(icon: SweetAlertIcon, title: string) {
        this.toast.fire({ icon, title });
    }

    success(message: string) {
        this.fire('success', message);
    }

    error(message: string) {
        this.fire('error', message);
    }

    info(message: string) {
        this.fire('info', message);
    }

    warning(message: string) {
        this.fire('warning', message);
    }

    // localized CRUD helpers — `item` is an already-translated entity label
    created(item: string) {
        this.success(this.translate.instant('created_success', { item }));
    }

    updated(item: string) {
        this.success(this.translate.instant('updated_success', { item }));
    }

    deleted(item: string) {
        this.success(this.translate.instant('deleted_success', { item }));
    }

    failed(error?: unknown) {
        // translate.get() (not instant()) because this can fire before the translation
        // file has finished loading — e.g. a 401 on the very first request at app bootstrap.
        this.translate.get(this.keyFor(error)).subscribe((message) => this.error(message));
    }

    private keyFor(error: unknown): string {
        if (!(error instanceof HttpErrorResponse)) {
            return 'operation_failed';
        }
        switch (error.status) {
            case 401:
                return 'error_unauthorized';
            case 403:
                return 'error_forbidden';
            case 404:
                return 'error_not_found';
            case 0:
                return 'error_network';
            default:
                return error.status >= 500 ? 'error_server' : 'operation_failed';
        }
    }
}
